import { and, eq } from "drizzle-orm";
import type { SQLiteColumn, SQLiteTable } from "drizzle-orm/sqlite-core";
import type { PowerSyncDrizzleDB } from "@agape/powersync";
import { nowISO } from "../utils/date-helpers";

/**
 * ── Local-first writes that CANNOT disappear silently ───────────────────
 *
 * A Drizzle `UPDATE … WHERE id = ?` against PowerSync's **view** can match
 * **ZERO rows and still return "success"**. The mechanism: when the CRUD queue
 * is drained, PowerSync **rebuilds the local view from the latest
 * checkpoint**. If the checkpoint does not yet contain the newly created row (e.g.
 * a service is created and, seconds later, renamed in the same session), the
 * row is **ABSENT at that instant** — and an UPDATE against it writes nothing,
 * creates no CRUD op, and **does not fail**. Measured and fixed earlier in
 * en `listto-app` (canon, `packages/shared/src/powersync/verified-write.ts` en
 * `origin/main`) and `space-academy-app` (certified with the wrong course
 * equivocado del curso).
 *
 * Why the absence of an error proves nothing here: the server write path
 * deliberately always returns 2xx (a 4xx would block the client queue
 * forever). This is a DIFFERENT failure mode — the server rejects silently —
 * but the outcome is the same: a user write disappears without
 * rastro.
 *
 * Module contract: write, **REREAD**, compare. If the value
 * did not land, retry with backoff; if it still has not landed after retries,
 * **THROW** — the caller reports it (toast) instead of leaving the user
 * looking at a field that does not represent anything saved.
 *
 * ── `user` IS covered (2026-08-13 audit, not an exception) ─────
 * Until the conversion in §4.11 (`40b6ef9`), `user` was excluded by copying the
 * canonical argument: "the server creates the row before the client can
 * write it." That is false in this repository — and remains false even though
 * `ThemeProvider` is no longer part of the problem (see below):
 *
 *   - `db` (the PowerSync accessor) becomes non-null as soon as the local
 *     SQLite is opened, not when the initial sync finishes.
 *   - `userId()` is enabled by the REST session (`signUpActor`/`signInActor`,
 *     `packages/shared/src/auth/controllers.ts`), a channel independent of
 *     sync stream, so the server row may not have arrived yet when a mutation
 *     attempts to write.
 *
 * These two points alone are enough: between `db`+`userId()` becoming ready
 * and the initial checkpoint bringing the server-created `user` row, there is
 * a window in which a normal `UPDATE` (change language, theme…) hits a row the
 * local view does not yet have — it matches ZERO rows and silently returns
 * "success". This window does not depend on anyone seeding a local row: it
 * is simply the view synchronizing, nothing more.
 *
 * (Historical context, now CLOSED) Until this same change, a third factor
 * exacerbated this same window for real accounts: `ThemeProvider`'s
 * `createEffect` de `ThemeProvider`
 * (`apps/web/src/providers/theme-provider.tsx`) insertaba localmente una fila
 * `user` "ghost" row with 3 columns for ANY `uid`, without distinguishing
 * a guest from a real account, competing with the real row the server had
 * already created and that was on its way via sync. The `uid !== VISITOR_ID`
 * guard (see the reasoning next to the guard in `theme-provider.tsx`) closed that
 * concrete root cause. **This does NOT make the `patchRowVerified` protection
 * for `user` unnecessary** — the two points above (`db` and
 * `userId()` ready before the checkpoint) still open the same window for any
 * early write, with or without a ghost row in the middle. Here, `patchRowVerified`
 * is generic checkpoint defense in depth, not a patch left behind from the
 * already-fixed bug — if it is ever removed, it should be because the window
 * above is proven not to exist, not because `ThemeProvider` stopped seeding
 * ghosts.
 *
 * The 6 mutations in `packages/shared/src/users/mutations.ts` and
 * `toggleOptIn` (`gamification_opt_in`, `devotional-section.tsx` — it did not live in
 * `users/mutations.ts`, which is why the portfolio audit had not
 * contado) pasan por `patchRowVerified`. Ver
 * `infra/guides/POWERSYNC_VERIFIED_WRITE.md` §4.1 y
 * `infra/reports/user-exclusion-audit/2026-08-13-summary.md`.
 *
 * ── THREE parts of `updated_at`, and they are THREE different things ─────────
 * They overlap in the column name but not in behavior — each
 * has its own flag, and none implies the others:
 *
 *   1. **Stamping** (`hasUpdatedAt`, default `true`): whether this helper
 *      writes `updated_at` in the `SET` of each attempt. Every table in this
 *      repository has the column, so no current caller needs
 *      `hasUpdatedAt: false` — the flag remains because it is part of the
 *      from canon, not a local requirement.
 *   2. **Ignoring the `updated_at` supplied by the `patch`** (always, without a flag):
 *      it is never compared or passed through to SET unchanged. Several mutations in
 *      this repository share the same `values` object between their INSERT and
 *      UPDATE branches (e.g. `saveVenue` in `church-detail-page.tsx`), and
 *      that object already carries its own `updated_at: nowIso()` for the INSERT. If
 *      it slipped through without being removed, it would be compared as a domain
 *      field against the FRESH `updated_at` this helper stamps on each attempt — which
 *      never matches — and the patch would ALWAYS fail for the wrong reason.
 *   3. **Verificar POR `updated_at`** (`verifyBy: "updated_at"`, opt-in,
 *      requires `hasUpdatedAt` other than `false`): use the stamped timestamp as
 *      proof of landing instead of comparing the patch by value.
 *      `bumpHeadcount` (asistencia offline-first, `meeting-detail-page.tsx`)
 *      deliberately uses an ATOMIC `sql\`MAX(0, COALESCE(headcount, 0) + ?)\``
 *      increment — read-add-write in JS would reopen the double-tap race
 *      that this SQL avoids. An SQL expression has no known expected value
 *      in advance: it cannot be compared by value after the reread. It is
 *      EXPLICIT, never automatic: a silent *fallback* would turn "I cannot
 *      compare" into "I accept it", masking precisely the cases where the patch
 *      should NOT be comparable (a caller error, a mistyped field). If omitted
 *      and the patch contains an SQL expression, value comparison can never
 *      match and the function ends by throwing `LocalWriteLostError` even though the write
 *      landed: **fail closed**, not a false green.
 *      aterrizado: **fallo cerrado**, no un verde falso.
 *
 * ── `scope`: tenant isolation (dormant in this repo, do not delete) ─────
 * Taken from `comandero-app`, where removing the `organization_id` condition
 * from the `WHERE` while converting to `patchRowVerified` opened cross-organization
 * writes. `agape-church-app` does not currently distribute rows from more than one
 * church to the same client (verified: no mutation in
 * `packages/shared/src/**\/mutations.ts` combines `id` with another tenant
 * condition in the `WHERE`), so no call in this repository uses it
 * yet. It lives here so that a future multi-church per account inherits it from
 * the mechanical conversion instead of reinventing it under pressure — the signal
 * that would activate it: a mutation that starts filtering by `church_id` /
 * `organization_id` IN ADDITION to `id`.
 *
 * ── What this module does NOT cover (deliberately) ─────────────────────────
 * Los DELETE duros (`removeRelation`, `removeImage`, `removeAssignment`,
 * `removePhoto`, `removeElement`, `removePart`, `unassign`, `removeGrant`,
 * `toggleCheckIn` in its deletion branch…) are not verified: their natural
 * check would be "the row is no longer there", and "absent" is precisely the
 * ambiguous state in this window — it is impossible to distinguish "deleted"
 * from "not yet arrived". Verifying them requires another signal (the CRUD
 * relectura.
 *
 * `waitForRowPresent` (dormant in this repository; do not delete): needed when the
 * UPDATE lives INSIDE a transaction (`db.transaction`) and cannot be retried
 * without sleeping while holding the lock. This repository does not use
 * `db.transaction` in any domain mutation — all are independent statements — so
 * `patchRowVerified` (which CAN retry after the fact) is enough even for the
 * sibling INSERT+UPDATE case without a transaction (`recordMovement` in
 * `inventory-page.tsx`: records the movement and then updates the item's
 * stock). The signal that would activate it: a mutation in this repository
 * envolver un UPDATE en `db.transaction`.
 *
 * `readMatchingRowVerified` (dormant in this repository; do not delete): for the
 * upsert that decides INSERT-vs-UPDATE with its own CONTENT-based SELECT (without
 * a known `id` up front) rather than by primary key — that branch falls into the
 * SAME window and, if the SELECT sees the row missing because of the checkpoint,
 * DUPLICATES instead of losing the data. No mutation in this
 * repository decides that way today (verified: all `create*Mutation`/`add*`
 * generate their `id` with `newId()`/`crypto.randomUUID()` and INSERT directly). The signal
 * that would activate it: a mutation that starts deciding INSERT-vs-UPDATE by
 * content instead of by a known `id`.
 */

/**
 * Backoff for retries. Total ≈ 6.05 s: more than enough for a checkpoint
 * round trip without leaving a quick interaction — marking attendance, renaming
 * a service — hanging forever if the row truly does not exist.
 */
export const VERIFIED_WRITE_BACKOFF_MS = [50, 100, 200, 400, 800, 1500, 3000] as const;

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/** Additional equality condition — tenant isolation in depth. */
export interface ScopeCondition {
  column: SQLiteColumn;
  value: unknown;
}

export interface VerifiedWriteOptions {
  /** Waits between retries. Exposed so tests do not take seconds. */
  backoffMs?: readonly number[];
  /**
   * Extra conditions the row must ALSO satisfy, in addition to `id` —
   * typically `organization_id`/`church_id`. Applied both to the UPDATE
   * and to the reread: a row that does not satisfy the scope is treated like
   * an absent row (see security note above). Dormant in this repo.
   */
  scope?: readonly ScopeCondition[];
  /**
   * Whether the table has an `updated_at` column. Defaults to `true`. With `false`,
   * `patchRowVerified` never puts it in the `SET`, and `verifyBy: "updated_at"`
   * becomes invalid (there is no column to compare against). Every table in
   * this repository has it.
   */
  hasUpdatedAt?: boolean;
  /**
   * How to verify that the write landed when the patch cannot be
   * compared by value (e.g. an atomic SQL increment expression). Mode
   * EXPLICIT, never automatic — see part 3 of the note above.
   * `"updated_at"` compares the timestamp this helper stamps on each attempt:
   * it proves THAT write landed, not that the final value is the
   * esperado. Requiere `hasUpdatedAt` distinto de `false`.
   */
  verifyBy?: "updated_at";
}

/** Error from a local-first write that did not land in the view. */
export class LocalWriteLostError extends Error {
  constructor(
    readonly rowId: string,
    readonly fields: readonly string[],
  ) {
    super(
      `La escritura local no aterrizó en la fila ${rowId} (campos: ${fields.join(", ")}). ` +
        `La fila no está en la vista de PowerSync o el valor no se guardó.`,
    );
    this.name = "LocalWriteLostError";
  }
}

/** Builds `eq(id) AND eq(scope1) AND …` — or just `eq(id)` with no scope. */
function idAndScopeCondition(idColumn: SQLiteColumn, id: string, scope: readonly ScopeCondition[]) {
  if (scope.length === 0) {
    return eq(idColumn, id);
  }
  return and(eq(idColumn, id), ...scope.map((s) => eq(s.column, s.value)));
}

/**
 * Reads the row by id (+ `scope`, if provided), or `undefined` if it is not in the
 * local view or does not satisfy the scope — the same result as "missing row", by
 * design (see the security note above).
 */
async function readRow(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  scope: readonly ScopeCondition[] = [],
): Promise<Record<string, unknown> | undefined> {
  // Drizzle cannot infer the shape of a generic `SQLiteTable`; the read
  // is by primary key, so the row is a flat record of columns.
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- see above
  const rows = (await db
    .select()
    .from(table)
    .where(idAndScopeCondition(idColumn, id, scope))
    .limit(1)) as unknown as Record<string, unknown>[];
  return rows[0];
}

/** `null` and `undefined` are the same "empty" value when compared with SQLite. */
const sameValue = (a: unknown, b: unknown): boolean => (a ?? null) === (b ?? null);

/**
 * **Verified** `UPDATE … WHERE id = ?`: writes, rereads, compares the patch
 * of the patch have not landed. Throws `LocalWriteLostError`
 * if they are still missing after the backoff is exhausted.
 *
 * If `options.hasUpdatedAt` is not `false` (the normal case), this function sets
 * `updated_at` on every attempt — any `updated_at` supplied by the
 * `patch` itself is ALWAYS ignored, both when writing and when comparing (see
 * the note above, part 2); the rest of the `patch` must contain only domain
 * fields. With `options.verifyBy === "updated_at"`, the patch is not compared by
 * value: it checks that this attempt's timestamp landed (see the note above,
 * part 3) — combining it with `hasUpdatedAt: false` is a configuration error
 * and throws immediately, before writing anything.
 */
export async function patchRowVerified(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  patch: Record<string, unknown>,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;
  const scope = options.scope ?? [];
  const hasUpdatedAt = options.hasUpdatedAt ?? true;
  if (options.verifyBy === "updated_at" && !hasUpdatedAt) {
    throw new Error(
      'verifyBy: "updated_at" is incompatible with hasUpdatedAt: false — table has no such column to compare.',
    );
  }
  const { updated_at: _ignoredUpdatedAt, ...domainPatch } = patch;

  for (let attempt = 0; ; attempt++) {
    const stampedAt = hasUpdatedAt ? nowISO() : undefined;
    await db
      .update(table)
      .set(hasUpdatedAt ? { ...domainPatch, updated_at: stampedAt } : domainPatch)
      .where(idAndScopeCondition(idColumn, id, scope));

    const row = await readRow(db, table, idColumn, id, scope);
    const landed =
      row !== undefined &&
      (options.verifyBy === "updated_at"
        ? sameValue(row.updated_at, stampedAt)
        : Object.entries(domainPatch).every(([key, value]) => sameValue(row[key], value)));
    if (landed) {
      return;
    }

    const wait = backoffMs[attempt];
    if (wait === undefined) {
      throw new LocalWriteLostError(id, Object.keys(domainPatch));
    }
    await sleep(wait);
  }
}

/**
 * Waits until the row EXISTS in the local view before touching it (+ `scope`,
 * if provided, with the same isolation criterion as `patchRowVerified`).
 * Dormant in this repository (see note above) — hosted for the day when a
 * mutation needs to wrap an UPDATE in `db.transaction`.
 */
export async function waitForRowPresent(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;
  const scope = options.scope ?? [];

  for (let attempt = 0; ; attempt++) {
    if (await readRow(db, table, idColumn, id, scope)) {
      return;
    }
    const wait = backoffMs[attempt];
    if (wait === undefined) {
      throw new LocalWriteLostError(id, ["<fila ausente>"]);
    }
    await sleep(wait);
  }
}

/**
 * Re-reads, with the same backoff as the rest of the module, a row identified
 * by CONTENT rather than by `id`. Dormant in this repository (see note
 * above) — hosted for the day when a mutation decides INSERT-vs-UPDATE
 * by content rather than by a known `id`.
 *
 * It is ALWAYS called before opening the transaction that decides INSERT-vs-UPDATE,
 * y NUNCA dentro de ella. A diferencia de `patchRowVerified`/
 * `waitForRowPresent`, exhausting the backoff is NOT an error here: genuine
 * absence is a VALID result, so this function returns `undefined`
 * instead of throwing.
 *
 * `read` must repeat the SAME query on every attempt.
 */
export async function readMatchingRowVerified<T>(
  read: () => Promise<T[]>,
  options: VerifiedWriteOptions = {},
): Promise<T | undefined> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;

  for (let attempt = 0; ; attempt++) {
    const [row] = await read();
    if (row) {
      return row;
    }
    const wait = backoffMs[attempt];
    if (wait === undefined) {
      return undefined;
    }
    await sleep(wait);
  }
}
